import React from 'react'
import Link from 'next/link'
import Header from '../components/public/shared/Header'
import Testimonial from '../components/shared/Testimonial'
import ItemList from '../components/shared/ItemList'
import Footer from '../components/public/shared/Footer'
import { isAuthenticated } from '../utils/auth'
import headerImg from '../public/static/img/for-companies.jpg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faLightbulb,
	faUserGraduate,
	faUsersClass,
} from '@fortawesome/pro-light-svg-icons'

import ScheduleCallButton from 'components/public/shared/ScheduleCallButton'
import HeaderTitle from 'components/shared/HeaderTitle'
import LogoList, { LogoProps } from 'components/shared/LogoList'

const basePath = process.env.BASE_PATH

interface ForCompaniesProps {
	authenticated: boolean
}

export const getServerSideProps = (ctx) => {
	return {
		props: {
			authenticated: isAuthenticated(ctx),
			title:
				'Let students solve your challenges. Receive solutions and get access to talent.',
			description:
				'Real-life challenge platform allowing students to gain relevant experience, educators to improve their course and companies to get their challenges solved.',
			url: `${basePath}/for-companies`,
			image: 'master_challenge_for_companies.png',
		},
	}
}

// Port in to using useState hooks, if you need state
class ForCompanies extends React.Component<ForCompaniesProps> {
	constructor(props: any) {
		super(props)
	}
	render() {
		const { authenticated } = this.props
		const howItWorks = [
			{
				title: "Browse the different 'Challenge Spaces'",
				text: "The 'Challenge Spaces' describe the type of challenges that can be solved as part of a course.",
			},
			{
				title: 'Submit a challenge',
				text: 'Feel free to formulate and submit challenges for one or more courses. Refine the challenge together with the course coordinator and get it published.',
			},
			{
				title: 'Get matched to a team',
				text: 'Receive a list of teams interested in solving your challenge. Rank those teams and wait for our algorithm to match you with a team.',
			},
			{
				title: 'Run the actual challenge',
				text: 'The actual challenge runs during a certain period. Typically it starts with a kick off meeting with your teams. At the end of the challenge teams present their recommendations.',
			},
		]

		const testimonials = [
			{
				quote:
					'The team that helped us in finding new business opportunities around retirement was very motivated and eager to support us. In a very short period they were able to come up with relevant new ideas we did not think of before. The presentation about their findings was well structured and based on data. We were very happy with the results!',
				personName: 'Bart Louwerenburg',
				personTitle: 'Business Developer at NN Group',
				img: '/static/img/bart.jpeg',
			},
			{
				quote:
					'Our Master Challenge team came from a diverse, international background which really helped bring different perspectives and creative ideas. They were committed, always on time with everything, and communicated very well. The students were very autonomous and did not require much guidance or hand-holding from our side, which meant the Master Challenge ended up being a great addition to our project that worked out very well. I believe it’s a low-risk, high-reward initiative that would be useful to most companies!',
				personName: 'Jason Luijckx',
				personTitle: 'Senior Partnerships Manager at Team Liquid',
				img: '/static/img/jason.png',
			},
			{
				quote:
					'We could not have expected such a great experience working with the Master Challenge program. The communication, process and overall experience was great. We were paired with a perfect team of enthusiastic students who truly went all out. It was a pleasure working with them but the greatest bonus is that our team built some incredible technology that will aid and advance our services significantly. Thank you team, thank you Master Challenge!',
				personName: 'Chris Dawe',
				personTitle: 'Co-Founder at Effect Network',
				img: '/static/img/chris.jpg',
			},
		]

		const logos: LogoProps[] = [
			{
				src: '/static/img/companies/McKinsey&Company.png',
				alt: 'McKinsey & Company.png',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/gospooky.svg',
				alt: 'GoSpooky',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/KPN-Logo.png',
				alt: 'KPN',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/Lalaland-logo.png',
				alt: 'Lalaland',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/agp_logo.png',
				alt: 'AGP',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/EY_logo_2019.svg.png',
				alt: 'EY',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/DANONE_LOGO.png',
				alt: 'DANONE',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/wetransfer.png',
				alt: 'Wetransfer',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/Team_Liquid_logo.png',
				alt: 'Team Liquid',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
			{
				src: '/static/img/companies/politieacademie_logo.png',
				alt: 'Politieacademie',
				maxWidth: '7rem',
				maxHeight: '5rem',
			},
		]

		return (
			<div className="wrapper">
				<Header authenticated={authenticated} />
				<main className="pt-20 relative z-20">
					<HeaderTitle
						title="For companies"
						subtitle="Let students solve your challenges. Receive solutions and get access to talent."
						headerImg={headerImg}
					>
						<Link href="/challenge-spaces" passHref>
							<button className="bg-yellow-300 text-lg md:text-xl text-black hover:bg-yellow-200 transition-colors duration-300 my-2 mr-4">
								Explore challenge spaces
							</button>
						</Link>
						<ScheduleCallButton className="border border-yellow-200 text-yellow-300 hover:border-yellow-300 text-xl mr-4 my-2" />
					</HeaderTitle>

					<LogoList
						title="Companies that worked with us before"
						logos={logos}
						numColumns={5}
					/>

					<div className="relative pt-16 pb-10 border-t bg-gradient-to-b from-gray-50">
						<div className="container mx-auto px-4 relative text-center leading-relaxed">
							<h2 className="text-3xl mb-4 font-sans font-bold text-gray-900">
								Submit short-term and real-life challenges
							</h2>
							<p className="mb-4 text-gray-700 mx-auto max-w-3xl text-lg">
								The Master Challenge platform allows you to submit short-term
								and real-life challenges. During a challenge a team of Master or
								Bachelor students will solve your real-life challenges, such as
								exploring new markets, validating new value propositions or
								exploring effective growth strategies.
							</p>
						</div>
					</div>

					<div className="relative z-10">
						<div className="container mx-auto px-4 relative z-10">
							<div className="relative max-w-5xl bg-white mx-auto rounded-lg overflow-hidden">
								<h2 className="text-2xl mb-12 text-center font-sans font-bold">
									What&apos;s in it for you?
								</h2>

								<div className="flex flex-wrap content-end">
									<div className="w-full md:w-1/3 mb-4 flex flex-col items-center text-center p-4">
										<FontAwesomeIcon
											icon={faUserGraduate}
											className="text-4xl text-purple-900 mb-4"
										/>
										<h3 className="mx-auto text-xl mb-4 font-bold text-purple-900">
											Connect
										</h3>
										<p className="mx-auto text-lg text-gray-700">
											Connect with students from relevant courses
										</p>
									</div>

									<div className="w-full md:w-1/3 mb-4 flex flex-col items-center text-center p-4">
										<FontAwesomeIcon
											icon={faLightbulb}
											className="text-4xl text-purple-900 mb-4"
										/>
										<h3 className="mx-auto text-xl mb-4 font-bold text-purple-900">
											Get solutions
										</h3>
										<p className="mx-auto text-lg">
											Get valuable solutions on your challenge and save time.
										</p>
									</div>

									<div className="w-full md:w-1/3 mb-4 flex flex-col items-center text-center p-4">
										<FontAwesomeIcon
											icon={faUsersClass}
											className="text-4xl text-purple-900 mb-4"
										/>
										<h3 className="mx-auto text-xl mb-4 font-bold text-purple-900">
											Recruit
										</h3>
										<p className="mx-auto text-lg">
											Get access to a talented and capable pool of students.
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="container mx-auto px-4 pb-12 relative z-10">
						<div className="flex flex-wrap items-center justify-center py-10">
							<Link href="/challenge-spaces" passHref>
								<button className="bg-yellow-300 text-black hover:bg-yellow-200 transition-colors duration-300 mr-4 my-2">
									Explore challenge spaces
								</button>
							</Link>
							<ScheduleCallButton className="border-gray-300 border duration-300 transition-colors hover:border-gray-800 hover:text-black text-gray-900 mr-4 my-2" />
						</div>
					</div>

					<div className="relative border-t">
						<div className="container mx-auto">
							<div className="px-4 relative w-full py-12 mx-auto max-w-5xl">
								<h2 className="text-3xl mb-10 font-sans font-bold text-center">
									How it works
								</h2>

								<ItemList items={howItWorks} color="purple-900" />
							</div>

							<div className="mb-10 px-4 relative w-full pb-12 md:pl-10">
								<h2 className="text-2xl mb-6 font-sans font-bold text-center">
									Testimonials
								</h2>

								<div className="flex flex-wrap -mx-2">
									{testimonials.map((testimonial, index) => {
										return (
											<div className="w-full md:w-1/3 px-4 pb-2" key={index}>
												<Testimonial
													testimonial={testimonial}
													color="purple-900"
												/>
											</div>
										)
									})}
								</div>
							</div>
						</div>
					</div>

					<div className="relative border-t bg-gradient-to-b from-gray-50">
						<div className="container mx-auto px-4 pb-16 pt-6 relative z-10">
							<div className="flex flex-wrap items-center justify-center py-10">
								<h2 className="font-sans font-bold text-center w-full text-black text-2xl md:mr-10 mb-2 md:w-auto md:text-left">
									Want to know more?
								</h2>
								<Link href="/challenge-spaces" passHref>
									<button className="bg-yellow-300 border border-yellow-300 text-black hover:bg-yellow-200 hover:border-yellow-200 transition-colors duration-300 mr-4 my-2">
										Explore challenge spaces
									</button>
								</Link>
								<ScheduleCallButton className="border-gray-300 border duration-300 transition-colors hover:border-gray-800 hover:text-black text-gray-900 mr-4 my-2" />
							</div>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		)
	}
}

export default ForCompanies
